import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { SendStudyChatMessageRequest } from "@/types/requests/studies/chat";
import { StudyChatMessage } from "@/types/resources/studies/chat";
import {
  GetStudyChatResponse,
  SendStudyChatMessageResponse,
} from "@/types/responses/studies/chat";

const useGetChat = (studyId: string, runId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<StudyChatMessage[]>([]);

  const client = useAxiosClient(`/studies/${studyId}/runs/${runId}/messages`);

  const getChat = useCallback(() => {
    client
      .get<GetStudyChatResponse>("")
      .then((response) => {
        setMessages(response.data.messages);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  // Re-enter the loading state during render (not in an effect) when the
  // study/run changes, so the UI shows loading while refetching.
  const inputsKey = `${studyId}|${runId}`;
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    setLoading(true);
    setError(null);
  }

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    getChat();
  }, [getChat]);

  useEffect(() => {
    getChat();
  }, [getChat]);

  return {
    error,
    getChat: refresh,
    loading,
    messages,
  };
};

const useSendChatMessage = (studyId: string, runId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient(`/studies/${studyId}/runs/${runId}/messages`);

  const sendMessage = useCallback(
    async (data: SendStudyChatMessageRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          SendStudyChatMessageResponse,
          AxiosResponse<SendStudyChatMessageResponse>,
          SendStudyChatMessageRequest
        >("", {
          message: data.message.trim(),
        });
        return response.data.messages;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    clearError,
    error,
    loading,
    sendMessage,
  };
};

export { useGetChat, useSendChatMessage };
